import { useState, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Trash2, Loader2, Camera, AlertTriangle, Check } from "lucide-react";
import ItemPicker from "./ItemPicker";

const PAYMENT_MODES = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "card", label: "Card" },
];

export default function EmergencyBuyForm({ items, vendors, locations, settings, onDone, onCancel }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [location, setLocation] = useState("");

  // Sync location once the locations prop arrives (parent fetches async).
  useEffect(() => {
    if (!location && locations.length > 0) {
      setLocation(locations[0]?.code || "");
    }
  }, [locations, location]);
  const [vendorId, setVendorId] = useState("");
  const [newVendorName, setNewVendorName] = useState("");
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [amountPaid, setAmountPaid] = useState("");
  const [paymentMode, setPaymentMode] = useState("cash");
  const [reason, setReason] = useState("");
  const [billPhoto, setBillPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const fileRef = useRef(null);

  const perBillCap = settings?.emergency_cap_per_bill ?? 5000;
  const dailyCap = settings?.emergency_cap_per_outlet_per_day ?? 15000;

  const addLine = (item) => {
    setLines(prev => [...prev, {
      sku: item.sku,
      item_name: item.name,
      base_uom: item.base_uom,
      purchase_uom_label: item.purchase_uom_label,
      base_per_purchase: item.base_per_purchase,
      qty: "",
      uom_entered: item.base_per_purchase ? "purchase" : "base",
      unit_cost: "",
    }]);
  };

  const updateLine = (i, patch) => setLines(prev => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const removeLine = (i) => setLines(prev => prev.filter((_, idx) => idx !== i));

  const totalValue = useMemo(() => {
    return lines.reduce((s, l) => {
      const qty = Number(l.qty) || 0;
      const cost = Number(l.unit_cost) || 0;
      return s + qty * cost;
    }, 0);
  }, [lines]);

  const overCap = totalValue > perBillCap;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("File exceeds 20 MB"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/staff/inventory/v2/uploads", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBillPhoto(data);
      toast.success("Bill photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const quickAddVendor = async () => {
    if (!newVendorName.trim()) { toast.error("Vendor name required"); return; }
    try {
      const { data } = await api.post("/staff/inventory/v2/vendors/quick", { name: newVendorName.trim() });
      toast.success(`Vendor "${data.name}" added`);
      setVendorId(data.id);
      setShowQuickAdd(false);
      setNewVendorName("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add vendor");
    }
  };

  const validate = () => {
    if (!date || !location) { toast.error("Date and location required"); return false; }
    if (!vendorId) { toast.error("Vendor required"); return false; }
    if (!amountPaid || Number(amountPaid) <= 0) { toast.error("Amount paid required"); return false; }
    if (!reason.trim()) { toast.error("Reason required"); return false; }
    if (lines.length === 0) { toast.error("Add at least one item"); return false; }
    for (const [i, l] of lines.entries()) {
      if (!l.qty || Number(l.qty) <= 0) { toast.error(`Line ${i + 1}: qty required`); return false; }
      if (!l.unit_cost || Number(l.unit_cost) <= 0) { toast.error(`Line ${i + 1}: unit cost required`); return false; }
    }
    return true;
  };

  const createBatch = async () => {
    if (!validate()) return null;
    try {
      const { data } = await api.post("/staff/inventory/v2/batches", {
        channel: "manual",
        movement_type: "PURCHASE_DIRECT",
        date,
        location,
        vendor_id: vendorId,
        bill_total: totalValue,
        amount_paid: Number(amountPaid),
        payment_mode: paymentMode,
        bill_photo_path: billPhoto?.path || null,
        reason,
        lines: lines.map(l => ({
          sku: l.sku,
          qty: Number(l.qty),
          uom_entered: l.uom_entered,
          unit_cost: Number(l.unit_cost),
          reason,
        })),
      });
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create batch");
      return null;
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    const batch = await createBatch();
    setSaving(false);
    if (batch) {
      toast.success(`Draft ${batch.batch_no} saved`);
      onDone();
      onCancel();
    }
  };

  const saveAndPost = async () => {
    setPosting(true);
    const batch = await createBatch();
    if (batch) {
      try {
        const { data } = await api.post(`/staff/inventory/v2/batches/${batch.id}/post`);
        if (data.needs_approval) {
          toast.warning(`Sent to approvals: ${data.rule_hits.map(h => h.rule).join(", ")}`);
        } else {
          toast.success(`Posted — ${data.ledger_rows.length} ledger row(s)`);
        }
        onDone();
        onCancel();
      } catch (err) {
        const d = err?.response?.data?.detail;
        toast.error(d?.errors ? d.errors.map(e => e.detail).join(" · ") : (typeof d === "string" ? d : "Post failed"));
      }
    }
    setPosting(false);
  };

  return (
    <div data-testid="emergency-form" className="space-y-4">
      {/* Warning banner if over cap */}
      {overCap && (
        <div className="flex items-start gap-2 p-3 border border-[hsl(6_62%_45%)] bg-[hsl(6_60%_95%)] text-sm text-[hsl(6_62%_45%)]">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            <b>₹{totalValue.toLocaleString("en-IN")}</b> exceeds the per-bill emergency cap of <b>₹{perBillCap.toLocaleString("en-IN")}</b>.
            This will require Director approval.
          </div>
        </div>
      )}

      {/* Header */}
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
          Date
          <input type="date" data-testid="eb-date" value={date} onChange={e => setDate(e.target.value)} className="tbb-inp mt-1 w-full" />
        </label>
        <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
          Location
          <select data-testid="eb-location" value={location} onChange={e => setLocation(e.target.value)} className="tbb-inp mt-1 w-full">
            {locations.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
        </label>
      </div>

      {/* Vendor */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">Vendor *</span>
          <button type="button" onClick={() => setShowQuickAdd(!showQuickAdd)} className="text-xs text-[hsl(var(--forest))] hover:underline">
            {showQuickAdd ? "Pick existing" : "+ Quick add local vendor"}
          </button>
        </div>
        {showQuickAdd ? (
          <div className="flex gap-2">
            <input
              data-testid="eb-new-vendor"
              value={newVendorName}
              onChange={e => setNewVendorName(e.target.value)}
              placeholder="Vendor name"
              className="tbb-inp flex-1"
            />
            <button type="button" onClick={quickAddVendor} className="px-3 py-2 bg-[hsl(var(--forest))] text-[hsl(var(--bg))] text-sm rounded">
              <Check size={14} />
            </button>
          </div>
        ) : (
          <select data-testid="eb-vendor" value={vendorId} onChange={e => setVendorId(e.target.value)} className="tbb-inp w-full">
            <option value="">— Select vendor —</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        )}
      </div>

      {/* Payment */}
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
          Amount paid (₹) *
          <input type="number" step="0.01" data-testid="eb-amount" value={amountPaid} onChange={e => setAmountPaid(e.target.value)} className="tbb-inp mt-1 w-full" />
        </label>
        <label className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
          Payment mode *
          <select data-testid="eb-payment" value={paymentMode} onChange={e => setPaymentMode(e.target.value)} className="tbb-inp mt-1 w-full">
            {PAYMENT_MODES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
      </div>

      {/* Reason */}
      <label className="block text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">
        Reason *
        <input data-testid="eb-reason" value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Stock-out – urgent, Vendor failed to deliver" className="tbb-inp mt-1 w-full" />
      </label>

      {/* Bill photo */}
      <div>
        <span className="text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))]">Bill photo</span>
        <div className="mt-1 flex items-center gap-3">
          <input ref={fileRef} type="file" accept="image/*,.pdf" capture="environment" onChange={handleFile} className="hidden" />
          <button
            type="button"
            data-testid="eb-photo-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 px-4 py-2 border border-[hsl(var(--rule))] text-sm"
          >
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
            {billPhoto ? "Change photo" : "Take / upload photo"}
          </button>
          {billPhoto && (
            <span className="text-xs text-[hsl(var(--forest))]">✓ {billPhoto.path.split("/").pop()}</span>
          )}
        </div>
      </div>

      {/* Item picker */}
      <ItemPicker items={items} onPick={addLine} />

      {/* Lines */}
      {lines.length > 0 && (
        <div className="border border-[hsl(var(--rule))] bg-[hsl(var(--bg-2))]">
          <div className="p-2 border-b border-[hsl(var(--rule))] text-xs uppercase tracking-wider text-[hsl(var(--ink-soft))] flex justify-between">
            <span>{lines.length} item{lines.length === 1 ? "" : "s"}</span>
            <span className={`font-semibold ${overCap ? "text-[hsl(6_62%_45%)]" : "text-[hsl(var(--ink))]"}`}>₹{totalValue.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
          </div>
          {lines.map((l, i) => (
            <div key={i} data-testid={`eb-line-${i}`} className="p-3 border-b border-[hsl(var(--rule))] last:border-b-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="font-semibold text-sm">{l.item_name}</div>
                  <div className="text-xs text-[hsl(var(--ink-soft))] font-mono">{l.sku}</div>
                </div>
                <button onClick={() => removeLine(i)} className="text-[hsl(6_62%_45%)] p-1"><Trash2 size={16} /></button>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2">
                <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                  Qty
                  <input type="number" step="0.01" data-testid={`eb-qty-${i}`} value={l.qty} onChange={e => updateLine(i, { qty: e.target.value })} className="tbb-inp mt-0.5 w-full text-sm" />
                </label>
                <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                  UoM
                  <select data-testid={`eb-uom-${i}`} value={l.uom_entered} onChange={e => updateLine(i, { uom_entered: e.target.value })} className="tbb-inp mt-0.5 w-full text-sm">
                    <option value="base">{l.base_uom}</option>
                    {l.base_per_purchase && <option value="purchase">{l.purchase_uom_label || "Pack"}</option>}
                  </select>
                </label>
                <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--ink-soft))]">
                  Cost / unit
                  <input type="number" step="0.01" data-testid={`eb-cost-${i}`} value={l.unit_cost} onChange={e => updateLine(i, { unit_cost: e.target.value })} className="tbb-inp mt-0.5 w-full text-sm" />
                </label>
              </div>
              {l.qty && Number(l.qty) > 0 && l.unit_cost && Number(l.unit_cost) > 0 && (
                <div className="mt-1 text-xs text-[hsl(var(--brass-2))]">
                  {l.qty} × ₹{Number(l.unit_cost).toLocaleString("en-IN")} = ₹{(Number(l.qty) * Number(l.unit_cost)).toLocaleString("en-IN")}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-2 sticky bottom-0 bg-[hsl(var(--bg))] py-3 -mx-4 px-4 border-t border-[hsl(var(--rule))]">
        <button data-testid="eb-save-draft" onClick={saveDraft} disabled={saving || posting || lines.length === 0} className="flex-1 py-3 text-sm border border-[hsl(var(--rule))] text-[hsl(var(--ink-soft))] disabled:opacity-50">
          {saving ? <Loader2 size={16} className="animate-spin inline" /> : "Save draft"}
        </button>
        <button data-testid="eb-save-post" onClick={saveAndPost} disabled={saving || posting || lines.length === 0} className="flex-1 py-3 text-sm btn-brass disabled:opacity-50">
          {posting ? <Loader2 size={16} className="animate-spin inline" /> : "Post"}
        </button>
      </div>
      <style>{`.tbb-inp{background:hsl(var(--bg));border:1px solid hsl(var(--rule));padding:0.55rem 0.75rem;font-size:0.9rem}.tbb-inp:focus{outline:1px solid hsl(var(--forest));border-color:hsl(var(--forest))}`}</style>
    </div>
  );
}
